import { useCallback, useEffect, useState } from 'react'
import {
  getPatientMedicalInfo,
  patchtPatientMedicalInfo
} from 'services/patient/patientService'
import { IPatientMedicalInfoResponse } from 'types/patient/types'
import { IPatientSurgeriesRequest } from 'types/patient-surgeries/type'
import { useParams } from 'react-router-dom'
import dayjs, { Dayjs } from 'dayjs'
import { trimStringInJSON } from 'utils/formatString'
import { usePreventNavigation } from 'navigation/preventNavigation'
import { useIsMobile } from 'utils/responsive-design'

const formsKeys: Array<MedicalInfoKeys> = [
  'birth_date',
  'birth_sex',
  'height_cm',
  'weight_kg',
  'is_smoker',
  'hypertension',
  'pr_interval',
  'has_murmur',
  'heart_failure',
  'ejection_fraction',
  'has_diabetes',
  'has_cvd',
  'has_atrial_fibrillation',
  'has_left_ventricular_hypertrophy',
  'has_hypertension_treatment',
  'has_parental_hypertension',
  'use_oxygen',
  'cardiopathy',
  'depression',
  'respiratory_diseases',
  'surgeries',
  'installation_date',
  'calibration_date',
  'return_date',
  'description'
]

export type MedicalInfoKeys = keyof IPatientMedicalInfoResponse

export type MedicalInfoFormKey = (typeof formsKeys)[number]

export type DateFieldKey =
  | 'birth_date'
  | 'installation_date'
  | 'calibration_date'
  | 'return_date'

type MedicalInfoFormState = Omit<
  Pick<IPatientMedicalInfoResponse, MedicalInfoFormKey>,
  DateFieldKey
> & {
  [K in DateFieldKey]: Dayjs | null
}

export type MedicalInfoFieldControl<K extends MedicalInfoFormKey> = [
  MedicalInfoFormState[K],
  React.Dispatch<React.SetStateAction<MedicalInfoFormState[K]>>
]

const dateFieldKeys = new Set<MedicalInfoFormKey>([
  'birth_date',
  'installation_date',
  'calibration_date',
  'return_date'
])

const normalizeDateFieldValue = (value: unknown) => {
  if (value == null) return null
  if (dayjs.isDayjs(value)) return value
  if (typeof value === 'string') return dayjs(value)
  if (value instanceof Date) return dayjs(value)

  return value
}

const normalizeScalarValue = (value: unknown) => {
  if (value === 'true') return true
  if (value === 'false') return false

  if (dayjs.isDayjs(value)) {
    return value.format('YYYY-MM-DD')
  }

  if (value instanceof Date) {
    return dayjs(value).format('YYYY-MM-DD')
  }

  return value ?? null
}

const normalizeSurgeryDate = (value: unknown) => {
  if (dayjs.isDayjs(value)) {
    return value.toISOString()
  }

  if (value instanceof Date) {
    return value.toISOString()
  }

  return value ?? null
}

const normalizeSurgeriesValue = (value: unknown) => {
  if (!Array.isArray(value)) {
    return []
  }

  return value.map(item => ({
    surgery_id: item?.surgery_id ?? null,
    surgery_type: item?.surgery_type ?? '',
    surgery_date: normalizeSurgeryDate(item?.surgery_date)
  }))
}

const areFieldValuesEqual = (
  key: MedicalInfoFormKey,
  currentValue: unknown,
  originalValue: unknown
) => {
  if (key === 'surgeries') {
    return (
      JSON.stringify(normalizeSurgeriesValue(currentValue)) ===
      JSON.stringify(normalizeSurgeriesValue(originalValue))
    )
  }

  return (
    normalizeScalarValue(currentValue) === normalizeScalarValue(originalValue)
  )
}

const buildInitialFormState = (
  source?: IPatientMedicalInfoResponse
): MedicalInfoFormState => {
  const initialState = {} as MedicalInfoFormState

  for (const key of formsKeys) {
    if (key === 'surgeries') {
      ;(initialState as any)[key] = source?.[key] ?? []
      continue
    }

    if (dateFieldKeys.has(key)) {
      ;(initialState as any)[key] = normalizeDateFieldValue(source?.[key])
      continue
    }

    ;(initialState as any)[key] = source?.[key] ?? null
  }

  return initialState
}

export const useMedicalInfo = () => {
  const [state, setState] = useState<IStateStdTypes>('idle')
  const { patientId } = useParams()
  const [medicalInfo, setMedicalInfo] = useState<IPatientMedicalInfoResponse>(
    {} as IPatientMedicalInfoResponse
  )
  const [isEdited, setIsEdited] = useState<boolean>(false)
  const [formState, setFormState] = useState<MedicalInfoFormState>(
    buildInitialFormState
  )
  const [expandedList, setExpandedList] = useState<Array<boolean | undefined>>(
    []
  )

  const isMobile = useIsMobile()

  const { setMessageToPrevent: preventNavigation } = usePreventNavigation()

  const setFieldValue = useCallback(
    <K extends MedicalInfoFormKey>(
      key: K,
      value: React.SetStateAction<MedicalInfoFormState[K]>
    ) => {
      setFormState(prev => {
        const nextValue =
          typeof value === 'function'
            ? (
                value as (
                  prevValue: MedicalInfoFormState[K]
                ) => MedicalInfoFormState[K]
              )(prev[key])
            : value

        return {
          ...prev,
          [key]: nextValue
        }
      })
    },
    []
  )

  const getFieldValue = useCallback(
    <K extends MedicalInfoFormKey>(key: K): MedicalInfoFormState[K] =>
      formState[key],
    [formState]
  )

  const getFieldControl = useCallback(
    <K extends MedicalInfoFormKey>(key: K): MedicalInfoFieldControl<K> => [
      formState[key],
      value => setFieldValue(key, value)
    ],
    [formState, setFieldValue]
  )

  const surgeries: IPatientSurgeriesRequest[] = Array.isArray(
    formState.surgeries
  )
    ? formState.surgeries
    : []

  const updateSurgeries = useCallback(
    (value: React.SetStateAction<IPatientSurgeriesRequest[]>) => {
      setFieldValue('surgeries', prev => {
        const safePrev = Array.isArray(prev) ? prev : []

        return typeof value === 'function'
          ? (
              value as (
                prevValue: IPatientSurgeriesRequest[]
              ) => IPatientSurgeriesRequest[]
            )(safePrev)
          : value
      })
    },
    [setFieldValue]
  )

  const saveData = async () => {
    setState('loading')
    const data: IPatientMedicalInfoResponse = {
      patient_id: patientId
    } as IPatientMedicalInfoResponse

    for (const key of formsKeys) {
      const value = formState[key]

      if (key === 'surgeries') {
        const surgeriesValue = Array.isArray(value) ? value : []
        data[key] = surgeriesValue.map(surgery => {
          const surgeryDate = surgery?.surgery_date

          return {
            ...surgery,
            surgery_date: dayjs.isDayjs(surgeryDate)
              ? surgeryDate.toISOString()
              : (surgeryDate ?? null)
          }
        }) as never
        continue
      }

      if (dayjs.isDayjs(value)) {
        const dateTimeValue = value.toISOString()
        data[key] = dateTimeValue.split('T')[0] as never
        continue
      }
      if (value === 'true' || value === 'false') {
        data[key] = (value === 'true') as never
        continue
      }

      data[key] = value as never
    }

    const trimData = trimStringInJSON(data)

    const response = await patchtPatientMedicalInfo(patientId ?? '', trimData)

    if (response.success) {
      setState('success')
      setMedicalInfo(trimData)
      updateValues(trimData)
      preventNavigation()
    } else {
      setState('error')
    }
  }

  const updateValues = useCallback((origin: IPatientMedicalInfoResponse) => {
    setFormState(buildInitialFormState(origin))
  }, [])

  useEffect(() => {
    const fetchPatientMedicalInfo = async () => {
      setState('loading')
      const response = await getPatientMedicalInfo(patientId ?? '')
      if (response.success) {
        setMedicalInfo(response.data as IPatientMedicalInfoResponse)
        updateValues(response.data as IPatientMedicalInfoResponse)
        setState('success')
      } else {
        setState('error')
      }
    }
    fetchPatientMedicalInfo()

    return () => {
      setIsEdited(false)
    }
  }, [patientId, updateValues])

  const isChanged = useCallback(
    (origin: IPatientMedicalInfoResponse) => {
      for (const key of formsKeys) {
        if (!areFieldValuesEqual(key, formState[key], origin[key])) {
          return true
        }
      }

      return false
    },
    [formState]
  )

  useEffect(() => {
    if (['success', 'error'].includes(state)) {
      const changed = isChanged(medicalInfo)
      setIsEdited(changed)
      preventNavigation(
        changed ? 'Você tem mudanças não salvas. Deseja sair?' : undefined
      )
    }
  }, [state, medicalInfo, preventNavigation, isChanged])

  const hasUnsavedSurgeriesChanges = !areFieldValuesEqual(
    'surgeries',
    formState.surgeries,
    medicalInfo.surgeries
  )

  return {
    expandedList,
    setExpandedList,
    surgeries,
    setFieldValue,
    getFieldValue,
    getFieldControl,
    saveData,
    state,
    isEdited,
    hasUnsavedSurgeriesChanges,
    isMobile,
    updateSurgeries
  }
}

export default useMedicalInfo
